import { z, type ZodSchema } from "zod";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AdminPermission =
  | "dashboard:read"
  | "conversations:read"
  | "contacts:read"
  | "contacts:write"
  | "tools:read"
  | "tools:write"
  | "models:read"
  | "models:write"
  | "limits:read"
  | "limits:write"
  | "settings:read"
  | "settings:write"
  | "audit:read"
  | "users:read"
  | "users:write"
  | "personas:read"
  | "personas:write"
  | "tenants:read"
  | "tenants:write"
  | "feature_flags:read"
  | "feature_flags:write"
  | "tenants:manage"
  | "admin:read";

export type FeatureFlagName =
  | "admin_dashboard"
  | "admin_conversations"
  | "admin_contacts"
  | "admin_tools"
  | "admin_models"
  | "admin_limits"
  | "admin_settings"
  | "admin_audit"
  | "admin_users"
  | "admin_personas"
  | "admin_tenants"
  | "admin_feature_flags";

export type DataClass =
  | "aggregate"
  | "customer_pii"
  | "customer_content"
  | "config";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type AdminEndpointKey =
  // Dashboard
  | "dashboard.stats"
  | "dashboard.insights"
  // Contacts
  | "contacts.list"
  | "contacts.get"
  | "contacts.update"
  | "contacts.invite"
  | "contacts.deactivate"
  | "contacts.reactivate"
  // Conversations
  | "conversations.list"
  | "conversations.get"
  | "conversations.messages"
  | "conversations.delete"
  // Tools
  | "tools.list"
  | "tools.get"
  | "tools.create"
  | "tools.update"
  | "tools.delete"
  // Models (DEPRECATED — use catalog.models.* / tenantEditor.models.*)
  | "models.list"
  | "models.get"
  | "models.create"
  | "models.update"
  | "models.delete"
  // Limits
  | "limits.get"
  | "limits.update"
  // Settings
  | "settings.list"
  | "settings.get"
  | "settings.update"
  | "settings.activePrompt"
  // Audit
  | "audit.list"
  // Users
  | "users.list"
  | "users.get"
  | "users.invite"
  | "users.update"
  | "users.hardDelete"
  | "users.deactivate"
  | "users.reactivate"
  | "users.sessions"
  | "users.sessions.revoke"
  | "users.sessions.revokeAll"
  | "users.activity"
  | "users.resetPassword"
  | "users.assignRole"
  // Personas
  | "personas.list"
  | "personas.get"
  | "personas.create"
  | "personas.update"
  | "personas.delete"
  // Tenants
  | "tenants.list"
  | "tenants.get"
  | "tenants.create"
  | "tenants.provision"
  | "tenants.update"
  | "tenants.delete"
  // Contacts - Tool Overrides
  | "contacts.toolOverrides.get"
  | "contacts.toolOverrides.update"
  | "contacts.toolOverrides.delete"
  // Assessment
  | "assessment.get"
  // Trace
  | "trace.recent"
  | "trace.lookup"
  // Experiments
  | "experiments.list"
  | "experiments.get"
  | "experiments.promote"
  | "experiments.reject"
  // Feature Flags
  | "featureFlags.list"
  | "featureFlags.toggle"
  | "featureFlags.defaults"
  | "featureFlags.get"
  | "featureFlags.delete"
  // Catalog - Models
  | "catalog.models.list"
  | "catalog.models.get"
  | "catalog.models.create"
  | "catalog.models.update"
  | "catalog.models.delete"
  | "catalog.models.sync"
  | "catalog.models.syncLog"
  | "catalog.models.gatewayStatus"
  | "catalog.models.profile"
  | "catalog.models.resetConfig"
  | "catalog.models.dependencies"
  | "catalog.models.openrouter"
  | "catalog.models.provision"
  | "catalog.models.deprovision"
  | "catalog.models.manualProvision"
  | "catalog.models.test"
  // Catalog - Tools
  | "catalog.tools.list"
  | "catalog.tools.get"
  | "catalog.tools.create"
  | "catalog.tools.update"
  | "catalog.tools.delete"
  // Catalog - Model Roles
  | "catalog.modelRoles.list"
  | "catalog.modelRoles.grouped"
  | "catalog.modelRoles.update"
  // Catalog - Personas
  | "catalog.personas.list"
  | "catalog.personas.get"
  | "catalog.personas.create"
  | "catalog.personas.update"
  | "catalog.personas.delete"
  // Tenant Editor
  | "tenantEditor.models.list"
  | "tenantEditor.models.update"
  | "tenantEditor.modelRoles.list"
  | "tenantEditor.modelRoles.update"
  | "tenantEditor.tools.list"
  | "tenantEditor.tools.update"
  // Platform Users (cross-tenant, superadmin)
  // Admin Me
  | "admin.me"
  // Bench Runner
  | "bench.runs.list"
  | "bench.runs.get"
  | "bench.runs.launch"
  | "bench.runs.cancel"
  | "bench.runs.annotate"
  | "bench.kpi"
  | "bench.kpi.collect"
  | "bench.legalBench.dataset"
  | "bench.pipelineSnapshot"
  | "bench.deploys"
  // Email Log
  | "emailLog.list"
  | "emailLog.preview"
  | "emailLog.resend"
  // Consent Audit
  | "consent.audit"
  | "consent.auditExport"
  | "consent.log"
  | "consent.logExport"
  // Access Requests
  | "accessRequests.list"
  | "accessRequests.stats"
  | "accessRequests.approve"
  | "accessRequests.reject"
  // Workflow Users (pending approval)
  | "workflowUsers.pending"
  | "workflowUsers.pendingCount"
  | "workflowUsers.approve"
  | "workflowUsers.reject"
  // Insights Vertical Dashboards
  | "insights.onboarding"
  | "insights.economics"
  | "insights.performance"
  | "insights.models"
  | "insights.activity"
  | "insights.engagement"
  | "insights.quality"
  | "insights.conversations"
  // Sprint 26 Fase 4 — Quality Alerts + Repair Telemetry
  | "insights.qualityAlerts.aggregate"
  | "insights.repairTelemetry.list"
  | "insights.repairTelemetry.aggregate"
  // Library (Libreria Privata)
  | "library.list"
  | "library.stats"
  | "library.delete";

export interface AdminEndpointMeta {
  path: string;
  method: HttpMethod;
  permission: AdminPermission;
  featureFlag?: FeatureFlagName;
  data_class: DataClass;
  responseSchema?: ZodSchema;
  healthProbeKey?: string;
  dependsOn?: AdminEndpointKey[];
}

export type AdminApiError = {
  kind:
    | "auth"
    | "forbidden"
    | "not_found"
    | "validation"
    | "rate_limit"
    | "server"
    | "schema"
    | "superadmin_policy"
    | "network";
  httpStatus: number;
  endpointKey: AdminEndpointKey;
  correlationId?: string;
  details?: string;
};

// ---------------------------------------------------------------------------
// Zod Schemas
// ---------------------------------------------------------------------------

const TokensByModelEntrySchema = z.object({
  model: z.string(),
  prompt_tokens: z.number(),
  completion_tokens: z.number(),
  spend: z.number(),
});

export type TokensByModelEntry = z.infer<typeof TokensByModelEntrySchema>;

const ToolHealthMetricSchema = z.object({
  tool_name: z.string(),
  total_calls: z.number(),
  success_count: z.number(),
  error_count: z.number(),
  timeout_count: z.number(),
  blocked_count: z.number(),
  fail_rate: z.number(),
  avg_latency_ms: z.number(),
});

const DailySpendPointSchema = z.object({
  date: z.string(),
  spend_usd: z.number(),
  request_count: z.number(),
});

export const DashboardStatsSchema = z.object({
  total_contacts: z.number(),
  active_contacts: z.number(),
  total_conversations: z.number(),
  active_conversations: z.number(),
  total_messages: z.number(),
  total_users: z.number(),
  active_users: z.number(),
  total_personas: z.number(),
  active_personas: z.number(),
  generated_at: z.string(),
  // Extended KPI fields (nullable — cards hidden when null)
  spend_today_usd: z.number().nullable().optional(),
  spend_month_usd: z.number().nullable().optional(),
  tokens_by_model: z.array(TokensByModelEntrySchema).nullable().optional(),
  error_rate_24h: z.number().nullable().optional(),
  total_tenants: z.number().nullable().optional(),
  active_tenants: z.number().nullable().optional(),
  // Sprint 3: Enhanced KPI
  cost_per_conversation: z.number().nullable().optional(),
  cost_per_message: z.number().nullable().optional(),
  tool_health: z.array(ToolHealthMetricSchema).nullable().optional(),
  daily_spend_7d: z.array(DailySpendPointSchema).nullable().optional(),
  // Inference stats (7d)
  total_inferences_7d: z.number().nullable().optional(),
  success_inferences_7d: z.number().nullable().optional(),
  failed_inferences_7d: z.number().nullable().optional(),
  avg_latency_ms_7d: z.number().nullable().optional(),
});

export type DashboardStats = z.infer<typeof DashboardStatsSchema>;

// -- Admin Me ----------------------------------------------------------------

const AdminSectionSchema = z.object({
  key: z.string(),
  label_key: z.string(),
  icon: z.string(),
  enabled: z.boolean(),
  reason_disabled: z.string().optional().nullable(),
});

const AdminCapabilitiesSchema = z.record(z.string(), z.boolean().optional());

export const AdminMeSchema = z.object({
  actor_role: z.string(),
  is_admin: z.boolean(),
  is_superadmin: z.boolean(),
  permissions: z.array(z.string()),
  capabilities: AdminCapabilitiesSchema.optional(),
  tenant_id: z.string(),
  email: z.string().optional(),
  display_name: z.string().optional(),
  sections: z.array(AdminSectionSchema),
  panel_type: z.enum(["tenant", "platform"]).optional(),
});

export type AdminMeResponse = z.infer<typeof AdminMeSchema>;

// -- Tenants -----------------------------------------------------------------

const TenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  settings: z.record(z.string(), z.unknown()).optional(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough();

const TenantsPageSchema = z.object({
  items: z.array(TenantSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
});

export type TenantItem = z.infer<typeof TenantSchema>;

// -- Personas ----------------------------------------------------------------

const PersonaSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  avatarUrl: z.string().nullable().optional(),
  systemPrompt: z.string(),
  language: z.string(),
  style: z.string(),
  allowedTools: z.array(z.string()),
  blockedTools: z.array(z.string()),
  canHandoff: z.boolean().optional(),
  handoffTargets: z.array(z.string()).optional(),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough();

const PersonasPageSchema = z.object({
  items: z.array(PersonaSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
});

export type PersonaItem = z.infer<typeof PersonaSchema>;

// ---------------------------------------------------------------------------
// Endpoint Registry
// ---------------------------------------------------------------------------

export const ADMIN_ENDPOINTS: Record<AdminEndpointKey, AdminEndpointMeta> = {
  // ---- Dashboard ----
  "dashboard.stats": {
    path: "/admin/dashboard/stats",
    method: "GET",
    permission: "dashboard:read",
    featureFlag: "admin_dashboard",
    data_class: "aggregate",
    responseSchema: DashboardStatsSchema,
    healthProbeKey: "dashboard",
  },
  "dashboard.insights": {
    path: "/admin/dashboard/insights",
    method: "GET",
    permission: "dashboard:read",
    featureFlag: "admin_dashboard",
    data_class: "aggregate",
  },

  // ---- Contacts ----
  "contacts.list": {
    path: "/admin/contacts",
    method: "GET",
    permission: "contacts:read",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
    healthProbeKey: "contacts",
  },
  "contacts.get": {
    path: "/admin/contacts/:id",
    method: "GET",
    permission: "contacts:read",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
  },
  "contacts.update": {
    path: "/admin/contacts/:id",
    method: "PUT",
    permission: "contacts:write",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
  },
  "contacts.invite": {
    path: "/admin/workflow/contacts/invite",
    method: "POST",
    permission: "contacts:write",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
  },
  "contacts.deactivate": {
    path: "/admin/workflow/contacts/:id/deactivate",
    method: "POST",
    permission: "contacts:write",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
  },
  "contacts.reactivate": {
    path: "/admin/workflow/contacts/:id/reactivate",
    method: "POST",
    permission: "contacts:write",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
  },
  "contacts.toolOverrides.get": {
    path: "/admin/contacts/:id/tool-overrides",
    method: "GET",
    permission: "contacts:read",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
  },
  "contacts.toolOverrides.update": {
    path: "/admin/contacts/:id/tool-overrides",
    method: "PUT",
    permission: "contacts:write",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
  },
  "contacts.toolOverrides.delete": {
    path: "/admin/contacts/:id/tool-overrides",
    method: "DELETE",
    permission: "contacts:write",
    featureFlag: "admin_contacts",
    data_class: "customer_pii",
  },

  // ---- Conversations ----
  "conversations.list": {
    path: "/admin/conversations",
    method: "GET",
    permission: "conversations:read",
    featureFlag: "admin_conversations",
    data_class: "customer_content",
    healthProbeKey: "conversations",
  },
  "conversations.get": {
    path: "/admin/conversations/:id",
    method: "GET",
    permission: "conversations:read",
    featureFlag: "admin_conversations",
    data_class: "customer_content",
  },
  "conversations.messages": {
    path: "/admin/conversations/:id/messages",
    method: "GET",
    permission: "conversations:read",
    featureFlag: "admin_conversations",
    data_class: "customer_content",
  },
  "conversations.delete": {
    path: "/admin/conversations/:id",
    method: "DELETE",
    permission: "conversations:read",
    featureFlag: "admin_conversations",
    data_class: "customer_content",
  },

  // ---- Tools ----
  "tools.list": {
    path: "/admin/tools",
    method: "GET",
    permission: "tools:read",
    featureFlag: "admin_tools",
    data_class: "config",
    healthProbeKey: "tools",
  },
  "tools.get": {
    path: "/admin/tools/:id",
    method: "GET",
    permission: "tools:read",
    featureFlag: "admin_tools",
    data_class: "config",
  },
  "tools.create": {
    path: "/admin/tools",
    method: "POST",
    permission: "tools:write",
    featureFlag: "admin_tools",
    data_class: "config",
  },
  "tools.update": {
    path: "/admin/tools/:id",
    method: "PATCH",
    permission: "tools:write",
    featureFlag: "admin_tools",
    data_class: "config",
  },
  "tools.delete": {
    path: "/admin/tools/:id",
    method: "DELETE",
    permission: "tools:write",
    featureFlag: "admin_tools",
    data_class: "config",
  },

  // ---- Models ----
  "models.list": {
    path: "/admin/models",
    method: "GET",
    permission: "models:read",
    featureFlag: "admin_models",
    data_class: "config",
    healthProbeKey: "models",
  },
  "models.get": {
    path: "/admin/models/:id",
    method: "GET",
    permission: "models:read",
    featureFlag: "admin_models",
    data_class: "config",
  },
  "models.create": {
    path: "/admin/models",
    method: "POST",
    permission: "models:write",
    featureFlag: "admin_models",
    data_class: "config",
  },
  "models.update": {
    path: "/admin/models/:id",
    method: "PATCH",
    permission: "models:write",
    featureFlag: "admin_models",
    data_class: "config",
  },
  "models.delete": {
    path: "/admin/models/:id",
    method: "DELETE",
    permission: "models:write",
    featureFlag: "admin_models",
    data_class: "config",
  },

  // ---- Limits ----
  "limits.get": {
    path: "/admin/limits",
    method: "GET",
    permission: "limits:read",
    featureFlag: "admin_limits",
    data_class: "config",
    healthProbeKey: "limits",
  },
  "limits.update": {
    path: "/admin/limits",
    method: "PUT",
    permission: "limits:write",
    featureFlag: "admin_limits",
    data_class: "config",
  },

  // ---- Settings ----
  "settings.list": {
    path: "/admin/settings",
    method: "GET",
    permission: "settings:read",
    featureFlag: "admin_settings",
    data_class: "config",
    healthProbeKey: "settings",
  },
  "settings.get": {
    path: "/admin/settings/:key",
    method: "GET",
    permission: "settings:read",
    featureFlag: "admin_settings",
    data_class: "config",
  },
  "settings.update": {
    path: "/admin/settings",
    method: "PUT",
    permission: "settings:write",
    featureFlag: "admin_settings",
    data_class: "config",
  },
  "settings.activePrompt": {
    path: "/admin/settings/active-prompt",
    method: "GET",
    permission: "settings:read",
    data_class: "config",
  },

  // ---- Audit ----
  "audit.list": {
    path: "/admin/audit-log",
    method: "GET",
    permission: "audit:read",
    featureFlag: "admin_audit",
    data_class: "aggregate",
    healthProbeKey: "audit",
  },

  // ---- Users ----
  "users.list": {
    path: "/admin/workflow/users",
    method: "GET",
    permission: "users:read",
    featureFlag: "admin_users",
    data_class: "customer_pii",
    healthProbeKey: "users",
  },
  "users.get": {
    path: "/admin/workflow/users/:id",
    method: "GET",
    permission: "users:read",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.invite": {
    path: "/admin/workflow/users/invite",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.update": {
    path: "/admin/workflow/users/:id",
    method: "PUT",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.hardDelete": {
    path: "/admin/workflow/users/:id",
    method: "DELETE",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.deactivate": {
    path: "/admin/workflow/users/:id/deactivate",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.reactivate": {
    path: "/admin/workflow/users/:id/reactivate",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.sessions": {
    path: "/admin/users/:id/sessions",
    method: "GET",
    permission: "users:read",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.sessions.revoke": {
    path: "/admin/users/:id/sessions/:sessionId",
    method: "DELETE",
    permission: "users:read",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.sessions.revokeAll": {
    path: "/admin/users/:id/sessions",
    method: "DELETE",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.activity": {
    path: "/admin/users/:id/activity",
    method: "GET",
    permission: "users:read",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.resetPassword": {
    path: "/admin/users/:id/reset-password",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "users.assignRole": {
    path: "/admin/users/:id/assign-role",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },

  // ---- Personas ----
  "personas.list": {
    path: "/admin/personas",
    method: "GET",
    permission: "personas:read",
    featureFlag: "admin_personas",
    data_class: "config",
    responseSchema: PersonasPageSchema,
    healthProbeKey: "personas",
  },
  "personas.get": {
    path: "/admin/personas/:id",
    method: "GET",
    permission: "personas:read",
    featureFlag: "admin_personas",
    data_class: "config",
  },
  "personas.create": {
    path: "/admin/personas",
    method: "POST",
    permission: "personas:write",
    featureFlag: "admin_personas",
    data_class: "config",
  },
  "personas.update": {
    path: "/admin/personas/:id",
    method: "PUT",
    permission: "personas:write",
    featureFlag: "admin_personas",
    data_class: "config",
  },
  "personas.delete": {
    path: "/admin/personas/:id",
    method: "DELETE",
    permission: "personas:write",
    featureFlag: "admin_personas",
    data_class: "config",
  },

  // ---- Tenants ----
  "tenants.list": {
    path: "/admin/tenants",
    method: "GET",
    permission: "tenants:read",
    featureFlag: "admin_tenants",
    data_class: "config",
    responseSchema: TenantsPageSchema,
    healthProbeKey: "tenants",
  },
  "tenants.get": {
    path: "/admin/tenants/:id",
    method: "GET",
    permission: "tenants:read",
    featureFlag: "admin_tenants",
    data_class: "config",
  },
  "tenants.create": {
    path: "/admin/tenants",
    method: "POST",
    permission: "tenants:write",
    featureFlag: "admin_tenants",
    data_class: "config",
  },
  "tenants.provision": {
    path: "/admin/tenants/provision",
    method: "POST",
    permission: "tenants:write",
    featureFlag: "admin_tenants",
    data_class: "config",
  },
  "tenants.update": {
    path: "/admin/tenants/:id",
    method: "PATCH",
    permission: "tenants:write",
    featureFlag: "admin_tenants",
    data_class: "config",
  },
  "tenants.delete": {
    path: "/admin/tenants/:id",
    method: "DELETE",
    permission: "tenants:write",
    featureFlag: "admin_tenants",
    data_class: "config",
  },

  // ---- Consent Audit ----
  "consent.audit": {
    path: "/admin/consent-audit",
    method: "GET",
    permission: "settings:read",
    data_class: "customer_pii",
  },
  "consent.auditExport": {
    path: "/admin/consent-audit/export",
    method: "GET",
    permission: "settings:read",
    data_class: "customer_pii",
  },
  "consent.log": {
    path: "/admin/consent-audit/log",
    method: "GET",
    permission: "settings:read",
    data_class: "customer_pii",
  },
  "consent.logExport": {
    path: "/admin/consent-audit/log/export",
    method: "GET",
    permission: "settings:read",
    data_class: "customer_pii",
  },

  // ---- Email Log ----
  "emailLog.list": {
    path: "/admin/email-log",
    method: "GET",
    permission: "settings:read",
    data_class: "aggregate",
  },
  "emailLog.preview": {
    path: "/admin/email-log/preview/:templateType",
    method: "GET",
    permission: "settings:read",
    data_class: "aggregate",
  },
  "emailLog.resend": {
    path: "/admin/email-log/resend/:userId",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },

  // ---- Feature Flags ----
  "featureFlags.list": {
    path: "/admin/settings/config/feature-flags",
    method: "GET",
    permission: "feature_flags:read",
    featureFlag: "admin_feature_flags",
    data_class: "config",
    healthProbeKey: "featureFlags",
  },
  "featureFlags.toggle": {
    path: "/admin/settings/config/feature-flags/:key",
    method: "PUT",
    permission: "feature_flags:write",
    featureFlag: "admin_feature_flags",
    data_class: "config",
  },

  // ---- Catalog - Models ----
  "catalog.models.list": {
    path: "/admin/catalog/models",
    method: "GET",
    permission: "models:read",
    data_class: "config",
  },
  "catalog.models.get": {
    path: "/admin/catalog/models/:id",
    method: "GET",
    permission: "models:read",
    data_class: "config",
  },
  "catalog.models.create": {
    path: "/admin/catalog/models",
    method: "POST",
    permission: "models:write",
    data_class: "config",
  },
  "catalog.models.update": {
    path: "/admin/catalog/models/:id",
    method: "PATCH",
    permission: "models:write",
    data_class: "config",
  },
  "catalog.models.delete": {
    path: "/admin/catalog/models/:id",
    method: "DELETE",
    permission: "models:write",
    data_class: "config",
  },
  "catalog.models.sync": {
    path: "/admin/catalog/models/sync",
    method: "POST",
    permission: "tenants:write",
    data_class: "config",
  },
  "catalog.models.syncLog": {
    path: "/admin/catalog/models/sync-log",
    method: "GET",
    permission: "models:read",
    data_class: "config",
  },
  "catalog.models.gatewayStatus": {
    path: "/admin/catalog/models/gateway-status",
    method: "GET",
    permission: "models:read",
    data_class: "config",
  },
  "catalog.models.profile": {
    path: "/admin/catalog/models/profile",
    method: "POST",
    permission: "models:write",
    data_class: "config",
  },
  "catalog.models.resetConfig": {
    path: "/admin/catalog/models/:id/reset-config",
    method: "POST",
    permission: "models:write",
    data_class: "config",
  },
  "catalog.models.dependencies": {
    path: "/admin/catalog/models/:id/dependencies",
    method: "GET",
    permission: "models:read",
    data_class: "config",
  },
  "catalog.models.openrouter": {
    path: "/admin/catalog/models/openrouter",
    method: "GET",
    permission: "tenants:read",
    data_class: "config",
  },
  "catalog.models.provision": {
    path: "/admin/catalog/models/provision",
    method: "POST",
    permission: "tenants:write",
    data_class: "config",
  },
  "catalog.models.deprovision": {
    path: "/admin/catalog/models/deprovision",
    method: "POST",
    permission: "tenants:write",
    data_class: "config",
  },
  "catalog.models.manualProvision": {
    path: "/admin/catalog/models/manual-provision",
    method: "POST",
    permission: "tenants:write",
    data_class: "config",
  },
  "catalog.models.test": {
    path: "/admin/catalog/models/test",
    method: "POST",
    permission: "models:read",
    data_class: "config",
  },

  // ---- Catalog - Model Roles ----
  "catalog.modelRoles.list": {
    path: "/admin/catalog/model-roles",
    method: "GET",
    permission: "tenants:read",
    data_class: "config",
  },
  "catalog.modelRoles.grouped": {
    path: "/admin/catalog/model-roles/grouped",
    method: "GET",
    permission: "tenants:read",
    data_class: "config",
  },
  "catalog.modelRoles.update": {
    path: "/admin/catalog/model-roles",
    method: "PUT",
    permission: "tenants:write",
    data_class: "config",
  },

  // ---- Catalog - Tools ----
  "catalog.tools.list": {
    path: "/admin/catalog/tools",
    method: "GET",
    permission: "tools:read",
    data_class: "config",
  },
  "catalog.tools.get": {
    path: "/admin/catalog/tools/:id",
    method: "GET",
    permission: "tools:read",
    data_class: "config",
  },
  "catalog.tools.create": {
    path: "/admin/catalog/tools",
    method: "POST",
    permission: "tools:write",
    data_class: "config",
  },
  "catalog.tools.update": {
    path: "/admin/catalog/tools/:id",
    method: "PATCH",
    permission: "tools:write",
    data_class: "config",
  },
  "catalog.tools.delete": {
    path: "/admin/catalog/tools/:id",
    method: "DELETE",
    permission: "tools:write",
    data_class: "config",
  },

  // ---- Catalog - Personas ----
  "catalog.personas.list": {
    path: "/admin/catalog/personas",
    method: "GET",
    permission: "personas:read",
    data_class: "config",
  },
  "catalog.personas.get": {
    path: "/admin/catalog/personas/:id",
    method: "GET",
    permission: "personas:read",
    data_class: "config",
  },
  "catalog.personas.create": {
    path: "/admin/catalog/personas",
    method: "POST",
    permission: "personas:write",
    data_class: "config",
  },
  "catalog.personas.update": {
    path: "/admin/catalog/personas/:id",
    method: "PATCH",
    permission: "personas:write",
    data_class: "config",
  },
  "catalog.personas.delete": {
    path: "/admin/catalog/personas/:id",
    method: "DELETE",
    permission: "personas:write",
    data_class: "config",
  },

  // ---- Tenant Editor ----
  "tenantEditor.models.list": {
    path: "/admin/tenants/:id/models",
    method: "GET",
    permission: "tenants:read",
    data_class: "config",
  },
  "tenantEditor.models.update": {
    path: "/admin/tenants/:id/models",
    method: "PATCH",
    permission: "tenants:write",
    data_class: "config",
  },
  "tenantEditor.modelRoles.list": {
    path: "/admin/tenants/:id/model-roles",
    method: "GET",
    permission: "tenants:read",
    data_class: "config",
  },
  "tenantEditor.modelRoles.update": {
    path: "/admin/tenants/:id/model-roles",
    method: "PUT",
    permission: "tenants:write",
    data_class: "config",
  },
  "tenantEditor.tools.list": {
    path: "/admin/tenants/:id/tools",
    method: "GET",
    permission: "tenants:read",
    data_class: "config",
  },
  "tenantEditor.tools.update": {
    path: "/admin/tenants/:id/tools",
    method: "PATCH",
    permission: "tenants:write",
    data_class: "config",
  },

  // ---- Assessment ----
  "assessment.get": {
    path: "/admin/assessment",
    method: "GET",
    permission: "settings:read",
    data_class: "aggregate",
  },

  // ---- Trace ----
  "trace.recent": {
    path: "/admin/trace/recent",
    method: "GET",
    permission: "tenants:read",
    data_class: "aggregate",
  },
  "trace.lookup": {
    path: "/admin/trace/:hash",
    method: "GET",
    permission: "tenants:read",
    data_class: "aggregate",
  },

  // ---- Experiments ----
  "experiments.list": {
    path: "/admin/experiments",
    method: "GET",
    permission: "settings:read",
    data_class: "config",
  },
  "experiments.get": {
    path: "/admin/experiments/:id",
    method: "GET",
    permission: "settings:read",
    data_class: "config",
  },
  "experiments.promote": {
    path: "/admin/experiments/:id/promote",
    method: "POST",
    permission: "settings:write",
    data_class: "config",
  },
  "experiments.reject": {
    path: "/admin/experiments/:id/reject",
    method: "POST",
    permission: "settings:write",
    data_class: "config",
  },

  // ---- Feature Flags (extended) ----
  "featureFlags.defaults": {
    path: "/admin/settings/config/feature-flags/defaults",
    method: "GET",
    permission: "tenants:read",
    data_class: "config",
  },
  "featureFlags.get": {
    path: "/admin/settings/config/feature-flags/:key",
    method: "GET",
    permission: "tenants:read",
    data_class: "config",
  },
  "featureFlags.delete": {
    path: "/admin/settings/config/feature-flags/:key",
    method: "DELETE",
    permission: "feature_flags:write",
    data_class: "config",
  },

  // ---- Admin Me ----
  "admin.me": {
    path: "/admin/me",
    method: "GET",
    permission: "admin:read",
    data_class: "config",
    responseSchema: AdminMeSchema,
    healthProbeKey: "me",
  },

  // ---- Bench Runner ----
  "bench.runs.list": {
    path: "/admin/bench/runs",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "bench.runs.get": {
    path: "/admin/bench/runs/:id",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "bench.runs.launch": {
    path: "/admin/bench/runs",
    method: "POST",
    permission: "admin:read",
    data_class: "config",
  },
  "bench.runs.cancel": {
    path: "/admin/bench/runs/:id/cancel",
    method: "POST",
    permission: "admin:read",
    data_class: "config",
  },
  "bench.runs.annotate": {
    path: "/admin/bench/runs/:id/annotate",
    method: "POST",
    permission: "admin:read",
    data_class: "config",
  },
  "bench.kpi": {
    path: "/admin/bench/kpi",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "bench.kpi.collect": {
    path: "/admin/bench/kpi/collect",
    method: "POST",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "bench.legalBench.dataset": {
    path: "/admin/bench/legal-bench/dataset",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "bench.pipelineSnapshot": {
    path: "/admin/bench/pipeline-snapshot",
    method: "GET",
    permission: "admin:read",
    data_class: "config",
  },
  "bench.deploys": {
    path: "/admin/bench/deploys",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },

  // ---- Access Requests ----
  "accessRequests.list": {
    path: "/admin/identity/access-requests",
    method: "GET",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "accessRequests.stats": {
    path: "/admin/identity/access-requests/stats",
    method: "GET",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "aggregate",
  },
  "accessRequests.approve": {
    path: "/admin/identity/access-requests/:id/approve",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "accessRequests.reject": {
    path: "/admin/identity/access-requests/:id/reject",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },

  // ---- Workflow Users (pending approval) ----
  "workflowUsers.pending": {
    path: "/admin/workflow/users/pending",
    method: "GET",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "workflowUsers.pendingCount": {
    path: "/admin/workflow/users/pending/count",
    method: "GET",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "aggregate",
  },
  "workflowUsers.approve": {
    path: "/admin/workflow/users/:id/approve",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },
  "workflowUsers.reject": {
    path: "/admin/workflow/users/:id/reject",
    method: "POST",
    permission: "users:write",
    featureFlag: "admin_users",
    data_class: "customer_pii",
  },

  // ---- Insights Vertical Dashboards ----
  "insights.onboarding": {
    path: "/admin/insights/onboarding",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.economics": {
    path: "/admin/insights/economics",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.performance": {
    path: "/admin/insights/performance",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.models": {
    path: "/admin/insights/models",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.activity": {
    path: "/admin/insights/activity",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.engagement": {
    path: "/admin/insights/engagement",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.quality": {
    path: "/admin/insights/quality",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.conversations": {
    path: "/admin/insights/conversations",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },

  // -- Sprint 26 Fase 4: Quality Alerts + Repair Telemetry --
  "insights.qualityAlerts.aggregate": {
    path: "/admin/insights/quality-alerts/aggregate",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.repairTelemetry.list": {
    path: "/admin/insights/repair-telemetry",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "insights.repairTelemetry.aggregate": {
    path: "/admin/insights/repair-telemetry/aggregate",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },

  // -- Library (Libreria Privata) --
  "library.list": {
    path: "/admin/library/documents",
    method: "GET",
    permission: "admin:read",
    data_class: "customer_content",
  },
  "library.stats": {
    path: "/admin/library/stats",
    method: "GET",
    permission: "admin:read",
    data_class: "aggregate",
  },
  "library.delete": {
    path: "/admin/library/documents/:id",
    method: "DELETE",
    permission: "settings:write",
    data_class: "customer_content",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Expand path template with params.
 * expandPath("/admin/contacts/:id", { id: "abc" }) => "/admin/contacts/abc"
 */
export function expandPath(
  pathTemplate: string,
  params?: Record<string, string>,
): string {
  if (!params) return pathTemplate;
  let result = pathTemplate;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`:${key}`, encodeURIComponent(value));
  }
  return result;
}

/**
 * Get endpoint metadata by key. Throws if key is invalid (compile-time
 * safety via AdminEndpointKey should prevent this at runtime).
 */
export function getEndpointMeta(key: AdminEndpointKey): AdminEndpointMeta {
  const meta = ADMIN_ENDPOINTS[key];
  if (!meta) {
    throw new Error(`Unknown admin endpoint: ${key}`);
  }
  return meta;
}
